import {
  AppError,
  FUNCTIONS,
  safeParseRpcResult,
  toAppError,
  type FunctionName,
  type RpcArgs,
  type RpcName,
  type RpcResult,
} from '@mt/contracts';
import type { z } from 'zod';
import { env } from '../config/env.ts';
import { supabase } from './supabase.ts';
import { reportError } from './telemetry.ts';

type ArgsFor<N extends RpcName> = RpcArgs[N] extends Record<string, never> ? [] : [RpcArgs[N]];

/**
 * Typed RPC call: named SQL arguments in, contract-validated JSON out.
 * Errors become AppError carrying a safe user-facing message.
 */
export async function rpc<N extends RpcName>(name: N, ...args: ArgsFor<N>): Promise<RpcResult<N>> {
  let response;
  try {
    response = await supabase().rpc(name, (args[0] ?? {}) as Record<string, unknown>);
  } catch (error) {
    throw toAppError(error);
  }
  const { data, error, status } = response;
  if (error) throw toAppError({ ...error, status });
  const parsed = safeParseRpcResult(name, status === 204 ? null : data);
  if (!parsed.success) {
    reportError('contract_mismatch', {
      rpc: name,
      issues: parsed.error.issues
        .slice(0, 3)
        .map((i) => `${i.path.join('.')}: ${i.message}`)
        .join('; '),
    });
    throw new AppError('UNKNOWN', 500);
  }
  return parsed.data;
}

/** An Edge Function error with its structured payload (e.g. per-activity validation issues). */
export class FunctionError extends AppError {
  readonly payload: Record<string, unknown>;
  constructor(error: AppError, payload: Record<string, unknown>) {
    super(error.code, error.status, error.detail);
    this.payload = payload;
  }
}

/** Call an Edge Function with the signed-in user's access token. */
export async function invoke<F extends FunctionName>(
  name: F,
  body: z.input<(typeof FUNCTIONS)[F]['request']>,
): Promise<z.infer<(typeof FUNCTIONS)[F]['response']>> {
  const { data: sessionData } = await supabase().auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new AppError('NOT_AUTHENTICATED', 401);
  let response: Response;
  try {
    response = await fetch(`${env.supabaseUrl}/functions/v1/${name}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, apikey: env.supabaseAnonKey, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (error) {
    throw toAppError(error);
  }
  const json = (await response.json().catch(() => null)) as Record<string, unknown> | null;
  if (!response.ok) {
    const err = (json?.error ?? {}) as { code?: string; message?: string };
    const appError = toAppError({ message: err.code, details: err.message, status: response.status });
    throw new FunctionError(appError, (json?.error as Record<string, unknown> | undefined) ?? {});
  }
  const parsed = FUNCTIONS[name].response.safeParse(json);
  if (!parsed.success) {
    reportError('contract_mismatch', { fn: name });
    throw new AppError('UNKNOWN', 500);
  }
  return parsed.data as z.infer<(typeof FUNCTIONS)[F]['response']>;
}
